import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Card,
  CardContent,
  Divider,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Chat as ChatIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import AvailabilityChecker from './AvailabilityChecker';
import { getAllFilterAssignments, FILTER_ORDER, isTourHidden } from '../config/filter-mappings';

const AvailabilityPage: React.FC = () => {
  const [showAvailabilityChecker, setShowAvailabilityChecker] = useState(false);
  const [showHiddenTours, setShowHiddenTours] = useState(false);
  
  // Separate tours into visible (with specific filters OR Cheeky Midweeky) and hidden (only "All" filter)
  const allAssignments = getAllFilterAssignments();
  // Visible tours: tours with specific filters beyond "All", OR Cheeky Midweeky (which should appear in results)
  const visibleTours = allAssignments.filter(a => 
    a.filters.length > 1 || a.tourName === 'Cheeky Midweeky'
  );
  // Hidden tours are those with only "All" filter, EXCEPT Cheeky Midweeky (which should appear in results)
  const hiddenTours = allAssignments.filter(a => 
    a.filters.length === 1 && 
    a.filters[0] === 'All' && 
    a.tourName !== 'Cheeky Midweeky'
  );

  const handleCheckAvailability = () => {
    setShowAvailabilityChecker(true);
  };

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        {/* Page Header */}
        <Box display="flex" alignItems="center" mb={3}>
          <CalendarIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              Tour Availability Checker
            </Typography>
            <Chip 
              label="Test Environment" 
              size="small" 
              color="warning" 
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </Box>

        {/* Instructions */}
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Purpose:</strong> This page is a test environment for the tour availability feature 
            that will be integrated into the live chatbot.
          </Typography>
          <Typography variant="body2" mt={1}>
            Click the "Check Availability" button below to test the availability checker widget.
          </Typography>
        </Alert>

        {/* Test Chat Interface */}
        <Paper elevation={3} sx={{ mb: 4, p: 3, backgroundColor: '#f5f5f5' }}>
          <Box display="flex" alignItems="center" mb={2}>
            <ChatIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6" color="text.secondary">
              Test Chat Interface
            </Typography>
          </Box>

          {/* Chat Bubbles */}
          <Box mb={3}>
            {/* User Message */}
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Card sx={{ maxWidth: '70%', backgroundColor: '#1976d2' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body1" color="white">
                    Do you have availability for tours next week?
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Bot Response */}
            <Box display="flex" justifyContent="flex-start" mb={2}>
              <Card sx={{ maxWidth: '70%', backgroundColor: 'white' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body1" gutterBottom>
                    I can help you check our tour availability! Let me show you our live availability calendar.
                  </Typography>
                  <Box mt={2}>
                    <Button
                      variant="contained"
                      onClick={handleCheckAvailability}
                      disabled={showAvailabilityChecker}
                      fullWidth
                    >
                      Check Availability
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Paper>

        {/* Availability Checker Widget */}
        {showAvailabilityChecker && (
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography variant="h5">
                Live Availability
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowAvailabilityChecker(false)}
              >
                Hide
              </Button>
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <AvailabilityChecker />
          </Paper>
        )}

        {/* Filters Management Section */}
        <Paper elevation={3} sx={{ mt: 4, p: 4 }}>
          <Box display="flex" alignItems="center" mb={3}>
            <FilterListIcon sx={{ fontSize: 30, mr: 2, color: 'primary.main' }} />
            <Typography variant="h5">
              Tour Filter Assignments
            </Typography>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              This table shows how tours are assigned to filters based on the CSV configuration.
              Filter order: {FILTER_ORDER.join(', ')}
            </Typography>
          </Alert>
          
          {/* Visible Tours Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Tour Name</strong></TableCell>
                  <TableCell><strong>Assigned Filters</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleTours
                  .sort((a, b) => {
                    // Sort by number of filters (tours with more filters first), then alphabetically
                    const filterDiff = b.filters.length - a.filters.length;
                    if (filterDiff !== 0) return filterDiff;
                    return a.tourName.localeCompare(b.tourName);
                  })
                  .map((assignment, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {assignment.tourName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {FILTER_ORDER.map(filter => {
                            const hasFilter = assignment.filters.includes(filter);
                            return (
                              <Chip
                                key={filter}
                                label={filter}
                                size="small"
                                color={hasFilter ? 'primary' : 'default'}
                                variant={hasFilter ? 'filled' : 'outlined'}
                                sx={{
                                  opacity: hasFilter ? 1 : 0.3,
                                  fontSize: '0.7rem'
                                }}
                              />
                            );
                          })}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Hidden Tours Section */}
          {hiddenTours.length > 0 && (
            <Box mt={3}>
              <Paper variant="outlined" sx={{ borderColor: 'warning.main' }}>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="space-between"
                  p={2}
                  sx={{ 
                    cursor: 'pointer',
                    backgroundColor: showHiddenTours ? 'action.hover' : 'transparent',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                  onClick={() => setShowHiddenTours(!showHiddenTours)}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <VisibilityOffIcon sx={{ color: 'warning.main' }} />
                    <Typography variant="h6" color="warning.main">
                      Hidden Tours ({hiddenTours.length})
                    </Typography>
                    <Chip 
                      label="Only 'All' filter" 
                      size="small" 
                      color="warning" 
                      variant="outlined"
                    />
                  </Box>
                  <IconButton size="small">
                    {showHiddenTours ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                
                <Collapse in={showHiddenTours}>
                  <Divider />
                  <Box p={2}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        These tours are completely hidden from availability results. 
                        They will not appear when users check availability, regardless of filter selection.
                      </Typography>
                    </Alert>
                    
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Tour Name</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {hiddenTours
                            .sort((a, b) => a.tourName.localeCompare(b.tourName))
                            .map((assignment, index) => (
                              <TableRow key={index} hover>
                                <TableCell>
                                  <Typography variant="body2">
                                    {assignment.tourName}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label="Hidden"
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Collapse>
              </Paper>
            </Box>
          )}
          
          <Box mt={2}>
            <Typography variant="caption" color="text.secondary">
              Visible tours: {visibleTours.length} | 
              Hidden tours: {hiddenTours.length} | 
              Total: {allAssignments.length}
            </Typography>
          </Box>
        </Paper>

        {/* Technical Notes */}
        <Paper elevation={1} sx={{ mt: 4, p: 3, backgroundColor: '#fafafa' }}>
          <Typography variant="h6" gutterBottom>
            Technical Notes
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            • Data is fetched directly from Rezdy API
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            • Results are cached for 15 minutes to reduce API calls
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            • Date selection limited to 30 days ahead
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            • Tours can be filtered by type (All, Full Day, Short Walks, Private, Tongariro, Ruapehu)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            • Clicking a tour opens the booking page in a new tab
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            • Alternative dates are suggested when all tours are fully booked
          </Typography>
        </Paper>

        {/* Future Integration Plan */}
        <Paper elevation={1} sx={{ mt: 3, p: 3, backgroundColor: '#e3f2fd' }}>
          <Typography variant="h6" gutterBottom color="primary">
            Next Steps for Live Integration
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>1. Auto-Detection:</strong> Chatbot will detect availability-related questions 
            and automatically show this widget
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>2. Backend Proxy:</strong> Move API calls to Firebase function to secure API key
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>3. System Prompt Update:</strong> Remove restriction on availability confirmation 
            since we now have real-time data
          </Typography>
          <Typography variant="body2">
            <strong>4. Widget Embedding:</strong> Integrate this component into the chat widget UI
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default AvailabilityPage;


